/**
 * Slug SEO-friendly per URL del sito pubblico.
 * Formato medico: id-nome-cognome (es. 1-mario-rossi)
 * Formato branca: nome-normalized (es. cardiologia)
 */

export const slugify = (str) => {
  if (str == null || typeof str !== 'string') return '';
  return str
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Slug per scheda medico: id-nome-cognome (sempre con id per risolvere l'API).
 */
export const getMedicoSlug = (medico) => {
  if (!medico || medico.id == null) return '';
  const nome = (medico.nome ?? medico.firstName ?? '').trim();
  const cognome = (medico.cognome ?? medico.lastName ?? '').trim();
  const namePart = [nome, cognome].filter(Boolean).join(' ');
  const slugName = slugify(namePart) || 'medico';
  return `${medico.id}-${slugName}`;
};

/**
 * Estrae l'id medico dallo slug (supporta "123" o "123-mario-rossi").
 */
export const parseMedicoSlugToId = (slugParam) => {
  if (slugParam == null || slugParam === '') return null;
  const first = slugParam.split('-')[0];
  const id = parseInt(first, 10);
  return Number.isNaN(id) ? null : id;
};

/**
 * Slug per branca/disciplina (solo nome).
 */
export const getBrancaSlug = (branca) => {
  if (!branca) return '';
  const name = typeof branca === 'string' ? branca : (branca.branca || '');
  return slugify(name) || 'specializzazione';
};

/**
 * Titolo medico per genere: idGenere === 2 (Femmina) → Dott.ssa, altrimenti Dott.
 */
export const getTitoloMedico = (medico) => {
  if (!medico) return 'Dott.';
  const idGenere = medico.idGenere ?? medico.id_genere;
  return idGenere === 2 ? 'Dott.ssa' : 'Dott.';
};

/**
 * Formatta orario per visualizzazione (es. 17:30).
 * Usa l'ora dal DB: stringa con ":" (es. "17:30", "17:30:00") → mostrata come "HH:mm".
 * Fallback: se arriva un numero (es. 17) lo tratta come ora intera "17:00".
 */
export const formatSlotTime = (value) => {
  if (value == null) return '';
  const s = String(value).trim();
  if (s.includes(':')) {
    const [h, m] = s.split(':');
    const hour = parseInt(h, 10);
    const min = parseInt(m || '0', 10);
    if (Number.isNaN(hour)) return s;
    return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  const num = parseInt(s.replace(/\D/g, ''), 10);
  if (Number.isNaN(num)) return s;
  if (num >= 0 && num <= 23) return `${String(num).padStart(2, '0')}:00`;
  if (num >= 100 && num < 2400) {
    const hour = Math.floor(num / 100);
    const min = num % 100;
    if (hour <= 23 && min < 60) return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  return s;
};

/**
 * Pulisce il nome della prestazione da suffissi aggiunti automaticamente (es. " - Videovisita",
 * " - Solo segreteria", importi tra parentesi) ma lascia intatto il nome vero come scritto in DB.
 * NON rimuove "videovisita" o "solo segreteria" quando fanno parte del nome (es. "Prima videovisita",
 * "Videovisita di controllo").
 */
export const formatPrestazioneNome = (nome) => {
  if (nome == null || typeof nome !== 'string') return '';
  return nome
    .replace(/\s*[-–—]\s*(Videovisita|videovisita)\s*$/gi, '')          /* suffisso " - Videovisita" in fondo */
    .replace(/\s*[-–—]\s*(Solo segreteria|solo segreteria)\s*$/gi, '')  /* suffisso " - Solo segreteria" in fondo */
    .replace(/\s*\([^)]*€[^)]*\)\s*/g, '')    /* (€ 50,00) o (50€) ovunque */
    .replace(/\s*\(\s*[\d.,]+\s*\)\s*/g, '')  /* (150.00) o (90,00) ovunque */
    .trim()
    .replace(/\s+/g, ' ') || 'Prestazione';
};

/** Importo: formato unico da utils/format (es. `50,00 €` o `Gratuita`). */
export { formatPrice as formatPrestazioneImporto } from './format';

